import { Platform } from 'react-native'

/**
 * Which billing rail owns the active consumer Pro entitlement. Mirrors the server `proProvider`.
 * 'google' / 'apple' identify the exact store; 'revenuecat' is a generic native fallback (store
 * unknown → route by the current OS).
 */
export type ProProvider = 'stripe' | 'google' | 'apple' | 'revenuecat' | null

const STORE_SUBSCRIPTIONS_URL = {
  ios: 'https://apps.apple.com/account/subscriptions',
  android: 'https://play.google.com/store/account/subscriptions',
} as const

/**
 * Subscription-management URL for the store that actually owns the sub. When `proProvider` names the
 * store ('google' / 'apple') we use it — important when the buying store and the current device differ
 * (bought on Android, now on an iPhone). Only when the store is unknown ('revenuecat' / null) do we
 * fall back to the current OS.
 */
export function getStoreSubscriptionsUrl(proProvider?: ProProvider): string {
  if (proProvider === 'google') return STORE_SUBSCRIPTIONS_URL.android
  if (proProvider === 'apple') return STORE_SUBSCRIPTIONS_URL.ios
  return Platform.OS === 'ios' ? STORE_SUBSCRIPTIONS_URL.ios : STORE_SUBSCRIPTIONS_URL.android
}

/**
 * "Manage subscription" routes to the rail that actually owns the sub: a Stripe sub → the Stripe
 * Customer Portal; anything else (RevenueCat store sub, or unknown) → the OS store settings. Routing
 * a store sub to the Stripe portal would open an empty portal; routing a Stripe sub to the store would
 * show nothing to manage — so this decision must follow `proProvider`, not the current platform.
 */
export function isStripeManagedProvider(proProvider: ProProvider): boolean {
  return proProvider === 'stripe'
}
